import { useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { Button } from '~/components/Button';
import { ColumnLayout } from '~/components/ColumnLayout';
import { IconButton } from '~/components/IconButton';
import { useUserRoles } from '~settings/api/queries/userRoles';
import { SelectLabelGroups } from '~settings/components/SelectLabelGroups';
import { SelectLabels } from '~settings/components/SelectLabels';
import { SelectUserRole } from '~settings/components/SelectUserRole';

interface RoleToResourcesProps {
  isEditing?: boolean;
  name: string;
}

export function RoleToResources({ isEditing, name }: RoleToResourcesProps) {
  const { t } = useTranslation('settings', { keyPrefix: 'user' });

  const { userType } = useWatch();

  // TODO: see if there's a way to get the count of roles without having to fetch the data
  const { userRoles } = useUserRoles({ targetUserType: userType });

  const { fields, append, remove } = useFieldArray({
    name,
  });

  const canRemove = fields.length > 1;
  const canAddMore = fields.length < (userRoles?.data?.length ?? 0);

  return (
    <ul className="flex flex-col gap-md">
      {fields.map((item, index) => (
        <li key={item.id}>
          <ColumnLayout>
            <SelectUserRole
              name={`${name}.${index}.role`}
              targetUserType={userType}
            />
            {userType === 'EXTERNAL' ? (
              <SelectLabels
                type={isEditing ? 'edit' : 'create'}
                name={`${name}.${index}.resources`}
              />
            ) : (
              <SelectLabelGroups
                name={`${name}.${index}.resources`}
                withSpecialGroups
              />
            )}
            {canRemove ? (
              <IconButton
                iconType="trash"
                size="xlarge"
                className="self-end"
                onClick={() => remove(index)}
                aria-label={t('removeRole')}
              />
            ) : null}
          </ColumnLayout>
        </li>
      ))}
      {canAddMore ? (
        <li className="flex items-center">
          <Button
            onClick={() => {
              append({ role: '', resources: [] });
            }}
            className="inline-flex items-center justify-center gap-md p-md no-underline [&_i]:text-body-lg"
            type="button"
            variant="tertiary"
          >
            <Icon type="plus" aria-hidden />
            {t('addRole')}
          </Button>
        </li>
      ) : null}
    </ul>
  );
}

RoleToResources.displayName = 'RoleToResources';
